// API client del dashboard de Atención Ciudadana.
// Backend: /api/v1/admin/citizen-reports/* (mcp_django, auth Bearer JWT).

import { ensureFreshToken } from '@/lib/tokenRefresh';
import type { Buzon, CitizenReport, CitizenStats, ConversationMsg, LegacyHeatmap } from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/citizen-reports';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as any).detail || `Django ${res.status}: ${res.statusText}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

export function getCitizenStats(): Promise<CitizenStats> {
  return djangoFetch<CitizenStats>(`${BASE}/stats`);
}

// Mapa de calor del histórico LEGADO (separado de los KPIs vivos).
export function getLegacyHeatmap(): Promise<LegacyHeatmap> {
  return djangoFetch<LegacyHeatmap>(`${BASE}/legacy/heatmap`);
}

// GeoJSON de los polígonos del geofence (municipio/centro-historico/interapas).
// Proxy read-only a ucognet — lo pintado = lo ruteado.
export function getPolygons(): Promise<any> {
  return djangoFetch<any>(`${BASE}/polygons`);
}

// ── Anti-spam: inhabilitar/habilitar un número (solo admin) ──
export interface BlockedReporterRow {
  phone: string;
  reason: string;
  blocked_by: string;
  active: boolean;
  created_at: string;
}

export function getBlocked(): Promise<BlockedReporterRow[]> {
  return djangoFetch<BlockedReporterRow[]>(`${BASE}/blocked`);
}

export function blockReporter(phone: string, reason: string): Promise<BlockedReporterRow> {
  return djangoFetch<BlockedReporterRow>(`${BASE}/blocked`, {
    method: 'POST', body: JSON.stringify({ phone, reason }),
  });
}

export function unblockReporter(phone: string): Promise<{ unblocked: number }> {
  return djangoFetch<{ unblocked: number }>(
    `${BASE}/blocked/${encodeURIComponent(phone)}`, { method: 'DELETE' },
  );
}

// ── Operativo (buzones / tickets / conversación / evidencia) ──

export function listBuzones(): Promise<Buzon[]> {
  return djangoFetch<Buzon[]>(`${BASE}/buzones`);
}

// Catálogo COMPLETO de dependencias (sin scope) — destinos de reasignación.
export function getDependencies(): Promise<string[]> {
  return djangoFetch<string[]>(`${BASE}/dependencies`);
}

export function listReports(params: {
  dependency?: string; status?: string; category?: string; limit?: number;
} = {}): Promise<CitizenReport[]> {
  const q = new URLSearchParams();
  if (params.dependency) q.set('dependency', params.dependency);
  if (params.status) q.set('status', params.status);
  if (params.category) q.set('category', params.category);
  q.set('limit', String(params.limit ?? 100));
  return djangoFetch<CitizenReport[]>(`${BASE}?${q.toString()}`);
}

export function getReportByFolio(folio: string): Promise<CitizenReport> {
  return djangoFetch<CitizenReport>(`${BASE}/by-folio/${encodeURIComponent(folio)}`);
}

export function getConversation(folio: string): Promise<ConversationMsg[]> {
  return djangoFetch<ConversationMsg[]>(
    `${BASE}/by-folio/${encodeURIComponent(folio)}/conversation`,
  );
}

export function updateStatus(input: {
  folio: string; status: string; dependency?: string; note?: string;
}): Promise<CitizenReport> {
  return djangoFetch<CitizenReport>(`${BASE}/status`, {
    method: 'PATCH', body: JSON.stringify(input),
  });
}

export function attachEvidence(
  folio: string, url: string, note = '',
): Promise<CitizenReport> {
  return djangoFetch<CitizenReport>(
    `${BASE}/by-folio/${encodeURIComponent(folio)}/evidence`,
    { method: 'POST', body: JSON.stringify({ url, note }) },
  );
}

// FASE 1 — Sube 1+ imágenes como ARCHIVO (multipart) y las adjunta al reporte.
// El backend guarda los bytes en el almacenamiento canónico (Bunny CDN, URL estable)
// y las agrega a evidence_images. NO se pone Content-Type: el browser arma el boundary.
export async function uploadReportEvidence(folio: string, files: File[]): Promise<CitizenReport> {
  const jwt = await getJwt();
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  const res = await fetch(
    `${DJANGO_PROXY}${BASE}/by-folio/${encodeURIComponent(folio)}/evidence-upload`,
    { method: 'POST', headers: { Authorization: `Bearer ${jwt}` }, body: fd },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as any).detail || `Django ${res.status}: ${res.statusText}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}
